import { computed, ComputedRef, nextTick, Ref, ref } from "vue";
import {
  getCategoryIDFromPath,
  getEntryIDFromPath,
} from "@/util/pathParser.js";
import { CategoryDocument } from "@/types/category.ts";
import { EntryDocument } from "@/types/entry.ts";
import { useStore } from "vuex";

import { CategoriesLayout } from "@/types/categories.ts";

export const key = Symbol("cardControls");

export interface ContextMenuItem {
  title: string;
  icon: string | string[];
  handler: () => Promise<void>;
  key: number;
}

export interface CardControlsApi {
  show: ComputedRef<boolean>;
  toggleIsLive: (item: EntryDocument | CategoryDocument) => Promise<void>;
  setRenamingCategoryId: (val: string | null) => void;
  renamingCategoryId: Ref<string | null>;
  setEditBackgroundCategoryId: (val: string | null) => void;
  editBackgroundCategoryId: Ref<string | null>;
  setCopyProfileId: (val: string | null) => void;
  copyProfileId: Ref<string | null>;
  selectedCards: Ref<string[]>;
  isSelectionMode: Ref<boolean>;
  isSelected: (id: string) => ComputedRef<boolean>;
  toggleIsSelected: (id: string) => void;
  clearSelectedCards: () => void;
  closeSelectionMode: () => void;
  createContextMenu: (
    item: ComputedRef<EntryDocument | CategoryDocument>
  ) => ComputedRef<ContextMenuItem[]>;
}

interface Patch {
  [key: string]: string | boolean;
}

export default function createData(): CardControlsApi {
  const store = useStore();

  function getItemId(item: EntryDocument | CategoryDocument) {
    if (["Categories", "SubCategories"].includes(store.state.route.name)) {
      return getCategoryIDFromPath(item.path);
    }
    return getEntryIDFromPath(item.path);
  }

  const patchEntryCard = async (item: EntryDocument, patch: Patch) => {
    const entryID = getItemId(item);
    const path = `/entry/${entryID}`;
    const { categoryID } = store.state.route.params;
    await store.dispatch("api/patchEntry", {
      categoryID,
      path,
      patch,
    });
  };

  const patchCategoryCard = async (item: CategoryDocument, patch: Patch) => {
    if (store.state.route.name === "SubCategories") {
      await store.dispatch("api/patchSubCategory", {
        parentCategoryID: item.parentCategoryID,
        path: item.path,
        patch,
      });
    } else if (store.state.route.name === "Categories") {
      await store.dispatch("api/patchCategory", {
        path: item.path,
        patch,
      });
    }
  };

  const show = computed(
    () =>
      store.getters.showCMS &&
      ["Category", "Categories", "SubCategories"].includes(
        store.state.route.name
      ) &&
      store.getters.getStoreVar("Category", "page", "layout", "layout") !==
        "CategoryCarousel"
  );

  function isResizeable(item: EntryDocument | CategoryDocument): boolean {
    if (item.imageObjectFit?.length === 0) {
      return false;
    }
    const { name } = store.state.route;
    if (["Categories", "SubCategories"].includes(name)) {
      let prop;
      if (name === "Categories") {
        prop = "categoriesLayout";
      }
      if (name === "SubCategories") {
        prop = "subcategoryLayout";
      }
      const layout = store.getters.getStoreVar(name, "page", "layout", prop);
      return !["CategoriesCardLayout3", "CategoriesPillLayout"].includes(
        layout
      );
    }
    return ![
      "PersonListCategoryLayout",
      "PersonListCategoryLayout2",
      "PersonBannersLayout",
      "PersonCircleCardCategoryLayout",
    ].includes(
      store.getters.getStoreVar("Category", "page", "layout", "layout")
    );
  }

  async function toggleIsLive(item: EntryDocument | CategoryDocument) {
    const patch = { isLive: !item.isLive };
    if ("categoryName" in item) {
      await patchCategoryCard(item as CategoryDocument, patch);
    } else {
      await patchEntryCard(item as EntryDocument, patch);
    }
  }

  const renamingCategoryId: Ref<string | null> = ref(null);

  function setRenamingCategoryId(val: string | null) {
    renamingCategoryId.value = val;
  }

  const editBackgroundCategoryId: Ref<string | null> = ref(null);

  function setEditBackgroundCategoryId(val: string | null) {
    editBackgroundCategoryId.value = val;
  }

  const copyProfileId: Ref<string | null> = ref(null);

  function setCopyProfileId(val: string | null) {
    copyProfileId.value = val;
  }

  const selectedCards: Ref<string[]> = ref([]);
  const isSelectionMode: Ref<boolean> = ref(false);

  function clearSelectedCards() {
    selectedCards.value = [];
  }

  function closeSelectionMode() {
    clearSelectedCards();
    isSelectionMode.value = false;
  }

  function toggleIsSelected(id: string) {
    if (selectedCards.value.includes(id)) {
      selectedCards.value = selectedCards.value.filter((i) => i !== id);
    } else {
      selectedCards.value = selectedCards.value.concat(id);
      isSelectionMode.value = true;
    }
  }

  function isSelected(id: string) {
    return selectedCards.value.includes(id);
  }

  function createContextMenuEntry(card: ComputedRef<EntryDocument>) {
    return computed(() =>
      [
        {
          title: "Select Profile",
          icon: "square-check",
          handler: async () => {
            toggleIsSelected(card.value._id);
          },
          key: 0,
        },
        {
          title: "Copy Profile",
          icon: ["fas", "clone"],
          handler: async () => {
            setCopyProfileId(null);
            await nextTick(() => setCopyProfileId(card.value._id));
          },
          key: 1,
        },
        {
          title: card.value.isLive ? "Unpublish" : "Publish",
          icon: card.value.isLive ? "eye-slash" : "eye",
          handler: async () => {
            await toggleIsLive(card.value);
          },
          key: 2,
        },
        {
          title: "Resize Image",
          icon: "expand",
          handler: async () => {
            const patch = {
              imageObjectFit:
                card.value.imageObjectFit === "cover" ? "contain" : "cover",
            };
            await patchEntryCard(card.value, patch);
          },
          key: 3,
        },
        {
          title: "Delete Profile",
          icon: ["fas", "trash"],
          key: 0,
          handler: async () => {
            await store.dispatch("api/deleteEntry", {
              categoryID: store.state.route.params.categoryID,
              path: card.value.path,
            });
          },
        },
      ].filter((menuItem: ContextMenuItem) => {
        if (menuItem.key === 3 && !isResizeable(card.value)) {
          return false;
        }
        return true;
      })
    );
  }

  function createContextMenuCategory(card: ComputedRef<CategoryDocument>) {
    return computed(() =>
      [
        {
          title: "Select Folder",
          icon: "square-check",
          handler: async () => {
            toggleIsSelected(card.value._id);
          },
          key: 0,
        },
        {
          title: "Rename Folder",
          icon: "edit",
          handler: async () => {
            setRenamingCategoryId(null);
            await nextTick(() => setRenamingCategoryId(card.value._id));
          },
          key: 1,
        },
        {
          title: "Edit Folder Cover",
          icon: "image",
          handler: async () => {
            setEditBackgroundCategoryId(null);
            await nextTick(() => setEditBackgroundCategoryId(card.value._id));
          },
          key: 2,
        },
        {
          title: "Copy Folder Structure",
          icon: ["fas", "clone"],
          handler: async () => {
            const { categoryName } = card.value;
            const sourceCategoryId = card.value.path.split("/")[2]; // category of the item
            const destCategoryId = store.state.route.params.categoryID;
            const data: Patch = {
              targetCategoryId: sourceCategoryId,
              routeName: store.state.route.name,
            };
            if (destCategoryId) {
              data.categoryId = destCategoryId;
            }
            const res = await store.dispatch("api/copyCategoryStructure", data);
            if (res.status === 200) {
              alert(`Successfully copied ${categoryName}`);
            } else {
              alert(
                "Error copying folder. Please check your internet connection. If the problem persists please contact support"
              );
            }
          },
          key: 3,
        },
        {
          title: card.value.isLive ? "Unpublish" : "Publish",
          icon: card.value.isLive ? "eye-slash" : "eye",
          handler: async () => {
            await toggleIsLive(card.value);
          },
          key: 4,
        },
        {
          title: "Resize Image",
          icon: "expand",
          handler: async () => {
            const patch = {
              imageObjectFit:
                card.value.imageObjectFit === "cover" ? "contain" : "cover",
            };
            await patchCategoryCard(card.value, patch);
          },
          key: 5,
        },
        {
          title: "Delete Folder",
          icon: ["fas", "trash"],
          key: 6,
          handler: async () => {
            if (store.state.route.params.categoryID) {
              await store.dispatch("api/deleteSubCategory", {
                path: card.value.path,
                categoryID: store.state.route.params.categoryID,
              });
            } else {
              await store.dispatch("api/deleteCategory", {
                path: card.value.path,
              });
            }
          },
        },
      ].filter((menuItem: ContextMenuItem) => {
        if (menuItem.key === 5 && !isResizeable(card.value)) {
          return false;
        }
        if (menuItem.key === 2) {
          /*
         We should only show the Edit Card Background option
         for Categories layouts that show it.
          */
          const layoutsWithCardBackground: CategoriesLayout[] = [
            "CategoriesCardLayout",
            "CategoriesCardLayout2",
            "CategoriesCardLayout3",
            "CategoriesCardLayout4",
          ];
          const currentPageLayout: CategoriesLayout =
            store.state.route.name === "Categories"
              ? store.getters.getStoreVar(
                  "Categories",
                  "page",
                  "layout",
                  "categoriesLayout"
                )
              : store.getters.getStoreVar(
                  "SubCategories",
                  "page",
                  "layout",
                  "subcategoryLayout"
                );
          return layoutsWithCardBackground.includes(currentPageLayout);
        }
        return true;
      })
    );
  }

  return {
    show,
    toggleIsLive,
    setRenamingCategoryId,
    renamingCategoryId,
    setEditBackgroundCategoryId,
    editBackgroundCategoryId,
    setCopyProfileId,
    copyProfileId,
    selectedCards,
    isSelectionMode,
    isSelected: (id) => computed(() => isSelected(id)),
    toggleIsSelected,
    clearSelectedCards,
    closeSelectionMode,
    createContextMenu(card: ComputedRef<EntryDocument | CategoryDocument>) {
      if ("categoryName" in card.value) {
        return createContextMenuCategory(card as ComputedRef<CategoryDocument>);
      }
      return createContextMenuEntry(card as ComputedRef<EntryDocument>);
    },
  };
}

const injectError = `Inject has failed for CardControlsApi`;

const selectedCards = ref([]);

export const defaultApi: CardControlsApi = {
  show: computed(() => false),
  toggleIsLive: async () => console.error(injectError),
  setRenamingCategoryId: () => console.error(injectError),
  renamingCategoryId: ref(null),
  setEditBackgroundCategoryId: () => console.error(injectError),
  editBackgroundCategoryId: ref(null),
  createContextMenu: () => {
    console.error(injectError);
    return computed(() => []);
  },
  isSelectionMode: ref(false),
  isSelected: () => {
    console.error(injectError);
    return computed(() => false);
  },
  toggleIsSelected: () => console.error(injectError),
  clearSelectedCards: () => console.error(injectError),
  closeSelectionMode: () => console.error(injectError),
  selectedCards,
  copyProfileId: ref(null),
  setCopyProfileId: () => console.error(injectError),
};

export const storybookApi: CardControlsApi = {
  show: computed(() => false),
  toggleIsLive: async (card) =>
    alert(`toggleIsLive for card with id ${card._id}`),
  setRenamingCategoryId: () => {},
  renamingCategoryId: ref(null),
  setEditBackgroundCategoryId: () => {},
  editBackgroundCategoryId: ref(null),
  createContextMenu: (card) =>
    computed(() => [
      {
        title: "Rename Folder",
        icon: "edit",
        key: 1,
        handler: async () =>
          alert(`rename folder clicked for id ${card.value._id}`),
      },
    ]),
  isSelectionMode: ref(false),
  isSelected: (id) => computed(() => id === "1"),
  toggleIsSelected: (id) => alert(`toggleIsSelected for id ${id}`),
  clearSelectedCards: () => {},
  closeSelectionMode: () => {},
  selectedCards,
  copyProfileId: ref(null),
  setCopyProfileId: () => {},
};
